/**
 * MapLibre GL map for the service-mapping CMS.
 *
 * Two uses: the editor embeds it to view and set ONE service's coordinates,
 * and the browser uses it to plot every service so gaps are visible — a service
 * with no point never appears, which is exactly what makes it findable.
 *
 * Tiles come from OpenStreetMap, so this is the one part of the service CMS
 * that touches the network. It still reads and writes only local data.
 *
 * MapLibre is driven imperatively rather than through a React wrapper: the map
 * owns its own view state, and markers are cheap to diff by hand, so a wrapper
 * would add a dependency without removing any of the lifecycle work below.
 */

import { useEffect, useMemo, useRef, useState } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Crosshair, MapPin } from "lucide-react"
import { type ServiceStore, type EntityKind, displayName } from "./types"

/**
 * Raster style pointing at OpenStreetMap's tiles.
 *
 * MapLibre normally renders vector tiles, which would need a tile provider and
 * an API key; a raster source keeps this key-free and uses the same tiles the
 * rest of the OSM ecosystem does.
 */
const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: "raster",
      tiles: [
        "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
        "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
        "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
      ],
      tileSize: 256,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
}

// Saturated colours, chosen to stay legible against OSM's busy street tiles.
const SELECTED = "#1d4ed8"
const OTHER = "#e11d48"
const DRAFT = "#f59e0b"

/**
 * Marker element — an inline SVG pin, so there are no image assets to resolve.
 *
 * The pin carries a white outline and a drop shadow because OSM tiles are pale
 * and detailed; without both, markers disappear into road and label clutter.
 * The selected pin also gets a pulsing halo so it is findable at a glance.
 */
// Colours come from the database, so only plain colour values go into the markup
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|[a-z]{3,20}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\))$/i

function pinElement(rawColor: string, selected: boolean): HTMLElement {
  const color = SAFE_COLOR.test(String(rawColor ?? "").trim()) ? String(rawColor).trim() : "#64748b"
  const el = document.createElement("div")
  const size = selected ? 46 : 34
  el.style.cursor = "pointer"
  el.style.width = `${size}px`
  el.style.height = `${size}px`
  el.innerHTML =
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" ` +
    `style="display:block;filter:drop-shadow(0 2px 3px rgba(0,0,0,.45))">` +
    (selected
      ? `<circle cx="12" cy="9" r="11" fill="${color}" opacity=".22">` +
        `<animate attributeName="r" values="8;11.5;8" dur="1.8s" repeatCount="indefinite"/>` +
        `<animate attributeName="opacity" values=".35;.08;.35" dur="1.8s" repeatCount="indefinite"/>` +
        `</circle>`
      : "") +
    `<path fill="${color}" stroke="#fff" stroke-width="2" stroke-linejoin="round" ` +
    `d="M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7z"/>` +
    `<circle cx="12" cy="9" r="3" fill="#fff"/></svg>`
  return el
}

export interface ServiceMapProps {
  store: ServiceStore
  locale: string
  /** Plot only these services; defaults to every service that has coordinates. */
  services?: any[]
  /** The service being edited, drawn highlighted and used to centre the view. */
  selectedId?: string | null
  /** Called when the user clicks the map or drags the highlighted pin. */
  onPick?: (lat: number, lon: number) => void
  onSelect?: (kind: EntityKind, id: string) => void
  height?: number | string
  className?: string
}

export function ServiceMap({
  store, locale, services, selectedId, onPick, onSelect, height = 260, className,
}: ServiceMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map())
  const [ready, setReady] = useState(false)

  // Handlers change identity every render; refs keep the map's listeners current
  // without tearing the map down and rebuilding it.
  const onPickRef = useRef(onPick)
  const onSelectRef = useRef(onSelect)
  useEffect(() => { onPickRef.current = onPick }, [onPick])
  useEffect(() => { onSelectRef.current = onSelect }, [onSelect])

  /**
   * `all` must be memoized on the STORE BUCKET, not recomputed inline.
   *
   * Object.values() returns a new array every render, so a `[all]` dependency
   * changed identity every time and defeated the memo below. That mattered:
   * `points` is a dependency of both the marker-rebuild effect and the
   * fit-bounds effect, so every unrelated parent render tore down and rebuilt
   * every marker AND snapped the viewport back to fit — visibly cancelling a
   * pan or zoom in progress.
   */
  const all = useMemo(
    () => services ?? Object.values(store.services),
    [services, store.services],
  )
  const points = useMemo(
    () => all.filter((s: any) => typeof s.lat === "number" && typeof s.lon === "number"),
    [all],
  )
  const missing = all.length - points.length

  const selected = selectedId ? store.services[selectedId] : null
  const selectedHasPoint =
    !!selected && typeof selected.lat === "number" && typeof selected.lon === "number"

  // ── Create the map once ────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OSM_STYLE,
      center: [0, 20],
      zoom: 1.5,
      attributionControl: { compact: true },
    })
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")

    /*
     * Wheel zoom is OFF by default, and only enabled while Ctrl/Cmd is held.
     *
     * The map sits partway down a long form inside a scrolling panel. With
     * scrollZoom on, any wheel gesture that happened to be over the map
     * zoomed it instead of scrolling the form -- so the form appeared to be
     * unscrollable, with no indication why. Requiring a modifier is the
     * conventional fix, and the +/- controls still zoom without one.
     */
    map.scrollZoom.disable()
    const onWheelKey = (e: KeyboardEvent | MouseEvent) => {
      if ((e as KeyboardEvent).ctrlKey || (e as KeyboardEvent).metaKey) map.scrollZoom.enable()
      else map.scrollZoom.disable()
    }
    window.addEventListener("keydown", onWheelKey)
    window.addEventListener("keyup", onWheelKey)
    map.on("click", e => {
      onPickRef.current?.(+e.lngLat.lat.toFixed(6), +e.lngLat.lng.toFixed(6))
    })
    map.on("load", () => setReady(true))
    mapRef.current = map

    return () => {
      window.removeEventListener("keydown", onWheelKey)
      window.removeEventListener("keyup", onWheelKey)
      markersRef.current.forEach(m => m.remove())
      markersRef.current.clear()
      map.remove()
      mapRef.current = null
    }
  }, [])

  // ── Sync markers with the data ─────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return

    const live = new Set<string>()

    for (const s of points as any[]) {
      live.add(s.id)
      const isSel = s.id === selectedId
      const color = isSel ? SELECTED : s.status === "draft" ? DRAFT : OTHER
      const draggable = isSel && !!onPickRef.current

      // Recreate rather than mutate: colour, size and draggability are all baked
      // into the marker at construction, and there are few enough to be cheap.
      const existing = markersRef.current.get(s.id)
      if (existing) existing.remove()

      const marker = new maplibregl.Marker({
        element: pinElement(color, isSel),
        anchor: "bottom",
        draggable,
      })
        .setLngLat([s.lon, s.lat])
        .setPopup(
          // Clear the pin's own height so the popup does not sit on top of it.
          new maplibregl.Popup({ offset: isSel ? 48 : 36, closeButton: false }).setText(
            displayName(s, locale, store.defaultLocale),
          ),
        )
        .addTo(map)

      marker.getElement().addEventListener("click", ev => {
        // Otherwise the map's own click handler would also fire and move the pin.
        ev.stopPropagation()
        if (!isSel) onSelectRef.current?.("service", s.id)
      })

      if (draggable) {
        marker.on("dragend", () => {
          const p = marker.getLngLat()
          onPickRef.current?.(+p.lat.toFixed(6), +p.lng.toFixed(6))
        })
      }

      markersRef.current.set(s.id, marker)
    }

    for (const [id, marker] of markersRef.current) {
      if (!live.has(id)) {
        marker.remove()
        markersRef.current.delete(id)
      }
    }
  }, [points, selectedId, ready, locale, store.defaultLocale])

  // ── Keep the viewport on the right place ───────────────────────────────────
  const lastCenter = useRef<string>("")
  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return

    if (selectedHasPoint && selected) {
      const key = `${selected.lat},${selected.lon}`
      // Only recentre when the target actually moves, so panning away from a
      // selected pin is not undone on every unrelated re-render.
      if (key !== lastCenter.current) {
        lastCenter.current = key
        map.easeTo({ center: [selected.lon, selected.lat], zoom: Math.max(map.getZoom(), 13), duration: 500 })
      }
      return
    }

    lastCenter.current = ""
    if (points.length > 0) {
      const b = new maplibregl.LngLatBounds()
      for (const s of points as any[]) b.extend([s.lon, s.lat])
      map.fitBounds(b, { padding: 40, maxZoom: 14, duration: 500 })
    }
  }, [selectedHasPoint, selected?.lat, selected?.lon, points, ready])

  // The map measures its container on creation; in a panel that was hidden or
  // resized that measurement is stale until it is told to remeasure.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !containerRef.current) return
    const ro = new ResizeObserver(() => map.resize())
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [ready])

  return (
    <div className={className}>
      <div
        ref={containerRef}
        style={{ height }}
        className="overflow-hidden rounded-md border bg-muted"
      />

      <div className="flex flex-wrap items-center gap-2 pt-1.5 text-[11px] text-muted-foreground">
        {onPick && (
          <span className="flex items-center gap-1">
            <Crosshair className="h-3 w-3" />
            {selectedHasPoint ? "Click the map or drag the pin to move it" : "Click the map to set a location"}
          </span>
        )}
        {/*
          Stated, because a map that ignores the wheel otherwise looks broken.
          The wheel is left to the surrounding form on purpose -- see the
          scrollZoom note where the map is created.
        */}
        <span className="text-muted-foreground/70">Ctrl-scroll to zoom</span>
        {/* Dataset-wide counts only make sense when plotting a set; editing one
            service, the same numbers read as a claim about that record. */}
        {all.length > 1 ? (
          <>
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {points.length} mapped
            </span>
            {missing > 0 && (
              <Badge variant="secondary" className="bg-amber-500/15 text-[10px] text-amber-700 dark:text-amber-400">
                {missing} with no location
              </Badge>
            )}
          </>
        ) : missing > 0 ? (
          <Badge variant="secondary" className="bg-amber-500/15 text-[10px] text-amber-700 dark:text-amber-400">
            No location set
          </Badge>
        ) : null}
      </div>
    </div>
  )
}

/** Coordinate readout plus a clear button, for the editor's Location section. */
export function CoordinateReadout({
  lat, lon, onClear,
}: { lat?: number; lon?: number; onClear: () => void }) {
  if (typeof lat !== "number" || typeof lon !== "number") {
    return <p className="text-[11px] text-muted-foreground">No coordinates set.</p>
  }
  return (
    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
      <span className="font-mono">{lat.toFixed(6)}, {lon.toFixed(6)}</span>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px]" onClick={onClear}>
        Clear
      </Button>
    </div>
  )
}
